import { useRef, useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getMcaDocApiData } from '../store/investor/service';
import { usePolling } from './commonUtil';
import { DATA_FETCH_STATUS } from '../store/dashboard/constants';
import { phoneIconGif } from '../assets/hostedassets';
import { normalizeNameKey } from './utils';

/**
 * A custom useEffect hook that only triggers on updates, not on initial mount
 * @param {Function} effect
 * @param {Array<any>} dependencies
 */

/**
 * Positions a dropdown using `position: fixed` relative to a trigger element.
 * Useful inside modals where absolute positioning fails.
 * Recalculates position on open and closes on outside scroll.
 *
 * @param isEnabled Enables or disables fixed dropdown behavior
 * @param isOpen Controls when position calculation and listeners are active
 * @param triggerRef Ref to the element the dropdown is aligned to
 * @param dropdownRef Optional ref to ignore scrolls inside the dropdown
 * @param onClose Callback fired when dropdown should close
 */

interface UseFixedDropdownInModalProps {
    isEnabled: boolean;
    isOpen: boolean;
    triggerRef: React.RefObject<HTMLElement>;
    dropdownRef?: React.RefObject<HTMLElement>;
    onClose?: () => void;
}

export const useFixedDropdownInModal = ({ isEnabled, isOpen, triggerRef, dropdownRef, onClose }: UseFixedDropdownInModalProps) => {
    const [position, setPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    });

    // Calculate fixed dropdown position//

    const calculatePosition = useCallback(() => {
        if (!isEnabled || !triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();

        setPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
        });
    }, [isEnabled, triggerRef]);

    //  Handle scroll close logic

    const handleScrollClose = useCallback(
        (event: Event) => {
            const target = event.target as HTMLElement;

            if (dropdownRef?.current && dropdownRef.current.contains(target)) {
                return;
            }

            onClose?.();
        },
        [dropdownRef, onClose],
    );

    // Recalculate position when dropdown opens
    useEffect(() => {
        if (isEnabled && isOpen) {
            calculatePosition();
        }
    }, [isEnabled, isOpen, calculatePosition]);

    // Attach scroll listener only when required
    useEffect(() => {
        if (!isEnabled || !isOpen) return;

        document.addEventListener('scroll', handleScrollClose, true);

        return () => {
            document.removeEventListener('scroll', handleScrollClose, true);
        };
    }, [isEnabled, isOpen, handleScrollClose]);

    return position;
};
export function useUpdateEffect(effect: Function, dependencies: any[] = []) {
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            return effect();
        }
    }, dependencies);
}

export const useApplyTheme = (uiStyle) => {
    useEffect(() => {
        if (uiStyle) {
            const root = document.documentElement;
            const buttons = uiStyle?.buttons;
            const tabs = uiStyle?.tabs;
            const texts = uiStyle?.texts;
            const backgrounds = uiStyle?.backgrounds;
            const borders = uiStyle?.borders;

            root.style.setProperty('--primary-button-bg', buttons?.primary?.backgroundColour);
            root.style.setProperty('--primary-button-text', buttons?.primary?.textColour);

            root.style.setProperty('--secondary-button-bg', buttons?.secondary?.backgroundColour);
            root.style.setProperty('--secondary-button-text', buttons?.secondary?.textColour);
            root.style.setProperty('--secondary-button-border', buttons?.secondary?.borderColour);

            root.style.setProperty('--tertiary-button-text', buttons?.tertiary?.textColour);

            root.style.setProperty('--tab-active-bg', tabs?.active?.backgroundColour);
            root.style.setProperty('--tab-active-text', tabs?.active?.textColour);

            root.style.setProperty('--tab-inactive-bg', tabs?.inactive?.backgroundColour);
            root.style.setProperty('--tab-inactive-text', tabs?.inactive?.textColour);

            root.style.setProperty('--primary-text-colour', texts?.primary?.textColour);

            root.style.setProperty('--primary-bg-colour', backgrounds?.primary?.backgroundColour);
            root.style.setProperty('--secondary-bg-colour', backgrounds?.secondary?.backgroundColour);

            root.style.setProperty('--primary-border-colour', borders?.primary?.borderColour);
        }
    }, [uiStyle]);
};

interface McaLoaderData {
    isFetching: boolean;
    reportStatus: typeof DATA_FETCH_STATUS.PROCESSING | typeof DATA_FETCH_STATUS.PROCESSED;
    updatedAt: string;
}

export const useMcaPolling = (orgId: string) => {
    const [mcaDocData, setMcaDocData] = useState<McaLoaderData | null>(null);
    const transformMcaLoaderData = useCallback((data: any) => {
        if (data) {
            const transformedData = {
                isFetching: data?.mcaDocApiData?.isFetching,
                reportStatus: data?.reportStatusFromOrgMaster?.reportStatus?.auditedFinancialsStatus,

                updatedAt: data?.mcaDocApiData?.updatedAt,
            };
            // Bail out of the re-render when a 10s poll tick brings back an unchanged loader
            // state — returning the previous reference keeps consumers stable.
            setMcaDocData((prev) =>
                prev &&
                prev.isFetching === transformedData.isFetching &&
                prev.reportStatus === transformedData.reportStatus &&
                prev.updatedAt === transformedData.updatedAt
                    ? prev
                    : transformedData,
            );
        }
    }, []);

    const getMCaApiDataToPoll = useCallback(async () => {
        const res = await getMcaDocApiData({ orgId: orgId });
        return res.data?.responseBody;
    }, [orgId]);

    const handleMcaPollingSuccess = useCallback(
        (data: any) => {
            transformMcaLoaderData(data);
        },
        [transformMcaLoaderData],
    );

    const shouldContinueMcaPolling = useCallback(
        (data: any) =>
            data?.reportStatusFromOrgMaster?.reportStatus?.auditedFinancialsStatus === 'Processing' &&
            data?.mcaDocApiData?.isFetching === true,
        [],
    );

    usePolling({
        fetchData: getMCaApiDataToPoll,
        onSuccess: handleMcaPollingSuccess,
        shouldContinue: shouldContinueMcaPolling,
        interval: 10000,
    });

    return { mcaDocData };
};

/**
 * Custom hook to animate phone icon GIF by re-rendering it every 2 seconds
 * @returns {string} src - The phone icon GIF URL with timestamp for cache busting
 */
export const usePhoneIconGif = (duration: number = 2000) => {
    const [src, setSrc] = useState(phoneIconGif);

    useEffect(() => {
        const interval = setInterval(() => {
            setSrc(`${phoneIconGif}?${Date.now()}`);
        }, duration);

        return () => clearInterval(interval);
    }, []);

    return src;
};

/**
 * Scrolls to + flashes a highlight on the row whose data attribute matches a URL search param.
 * Used to deep-link from Management Profile columns to a specific director row in the
 * destination tab (Related Parties, Legal Cases, etc.).
 *
 * The hook applies the GLOBAL `.RowFocused` class (defined in scss/index.scss) so destination
 * modules with their own sticky-column backgrounds can opt into the highlight via
 * `:global(.RowFocused)` selectors. Consumer is responsible for:
 *  - adding the `data-<attr>` attribute to each candidate row
 *  - normalising both the URL param value AND the row attribute via `normalizeNameKey`
 *  - calling this hook *after* data is rendered (`ready` flag)
 *
 * The hook clears the matched param (and `section`) from the URL on success so re-renders don't
 * loop and subsequent in-tab navigation isn't sticky.
 *
 * @param ready - Pass true once the destination list has rendered (e.g. data loaded, viewState === DATA)
 * @param paramKey - URL search-param key to read (e.g. 'directorName')
 * @param dataAttr - DOM data attribute on candidate rows (e.g. 'rp-director', 'lc-director')
 * @param fallbackParamKey - Optional secondary param + data attribute pair (e.g. DIN lookup if name miss)
 * @param fallbackDataAttr - Optional secondary data attribute matched against fallbackParamKey
 */
const ROW_FOCUS_HIGHLIGHT_CLASS = 'RowFocused';
export const useFocusRowFromUrlParam = ({
    ready,
    paramKey,
    dataAttr,
    fallbackParamKey,
    fallbackDataAttr,
    clickOnMatch = false,
}: {
    ready: boolean;
    paramKey: string;
    dataAttr: string;
    fallbackParamKey?: string;
    fallbackDataAttr?: string;
    clickOnMatch?: boolean;
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const primary = searchParams.get(paramKey) ?? '';
    const fallback = fallbackParamKey ? searchParams.get(fallbackParamKey) ?? '' : '';

    useEffect(() => {
        if (!ready) return;
        if (!primary && !fallback) return;
        const primaryTarget = normalizeNameKey(primary);
        const fallbackTarget = fallback.trim();

        const POLL_INTERVAL_MS = 300;
        const MAX_ATTEMPTS = 17;
        let attempts = 0;
        let pollId: number | undefined;

        const findRow = (): HTMLElement | null => {
            if (fallbackTarget && fallbackDataAttr) {
                const r = document.querySelector(`[data-${fallbackDataAttr}="${CSS.escape(fallbackTarget)}"]`) as HTMLElement | null;
                if (r) return r;
            }
            if (primaryTarget) {
                return document.querySelector(`[data-${dataAttr}="${CSS.escape(primaryTarget)}"]`) as HTMLElement | null;
            }
            return null;
        };

        const focusRow = (row: HTMLElement) => {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            row.classList.add(ROW_FOCUS_HIGHLIGHT_CLASS);
            setTimeout(() => row.classList.remove(ROW_FOCUS_HIGHLIGHT_CLASS), 2500);
            if (clickOnMatch) row.click();
            const next = new URLSearchParams(searchParams);
            next.delete(paramKey);
            if (fallbackParamKey) next.delete(fallbackParamKey);
            next.delete('section');
            setSearchParams(next, { replace: true });
        };

        const tryFocus = () => {
            const row = findRow();
            if (row) {
                focusRow(row);
                return;
            }
            attempts += 1;
            if (attempts < MAX_ATTEMPTS) {
                pollId = window.setTimeout(tryFocus, POLL_INTERVAL_MS);
            }
        };

        const rafId = requestAnimationFrame(tryFocus);

        return () => {
            cancelAnimationFrame(rafId);
            if (pollId !== undefined) clearTimeout(pollId);
        };
    }, [ready, primary, fallback]);
};

/**
 * FLIP animation for a keyed list of rows.
 *
 * Usage:
 *   const { rowRef } = useFlipRows(items);
 *   <tr ref={rowRef(col.field)}>…</tr>
 *
 * Pattern mirrors ModuleRail.tsx — capture positions before render (via
 * useLayoutEffect reading prevRectsRef), compute delta, apply inverse
 * translateY, force reflow, then transition to zero.
 *
 * Respects prefers-reduced-motion: skips the transform, leaving the
 * highlight-only effect (applied by the caller via a CSS class).
 */
export function useFlipRows<T>(items: T[], enabled = true) {
    const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
    const prevRectsRef = useRef<Map<string, DOMRect>>(new Map());

    useLayoutEffect(() => {
        if (!enabled) {
            rowRefs.current.forEach((el) => {
                el.style.transition = '';
            });
            prevRectsRef.current = new Map();
            return;
        }
        const prev = prevRectsRef.current;

        const next = new Map<string, DOMRect>();
        rowRefs.current.forEach((el, key) => {
            if (el && document.body.contains(el)) {
                next.set(key, el.getBoundingClientRect());
            }
        });

        const reducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!reducedMotion) {
            next.forEach((newRect, key) => {
                const oldRect = prev.get(key);
                const el = rowRefs.current.get(key);
                if (!oldRect || !el) return;
                const dy = oldRect.top - newRect.top;
                if (Math.abs(dy) < 2) return;
                el.style.transition = 'none';
                el.style.transform = `translateY(${dy}px)`;
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                el.offsetHeight;
                requestAnimationFrame(() => {
                    el.style.transition = 'transform 230ms ease-out';
                    el.style.transform = '';
                });
            });
        }

        prevRectsRef.current = next;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, enabled]);

    const rowRef = (key: string) => (el: HTMLTableRowElement | null) => {
        if (el) {
            rowRefs.current.set(key, el);
        } else {
            rowRefs.current.delete(key);
        }
    };

    return { rowRef };
}
